import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Box,
  FormControlLabel,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  card_border_radius,
  card_padding,
  compliance_setting_card_min_width,
} from "styles/Dimensions";
import { NumberInput } from "./NumberInput";
import { ComplianceSetting } from "generated/compliancesetting/compliancesetting_pb";

import { Radio, RadioGroup } from "@verily-src/react-design-system";

type ECGReadingsOptions = "NotApplicable" | "ECGReadingsPerDay";

// Define a component to set ECG readings compliance parameters
interface ECGReadingsSettingCardProps {
  initValue: ComplianceSetting | null;
}

// Defines an interface for the functions exposed from ECGReadingSettingsCard
export interface ECGReadingsSettingCardRef {
  validateInput: () => boolean;
  getECGReadingsPerDay: () => number;
}

export const ECGReadingSettingsCard = forwardRef<
  ECGReadingsSettingCardRef,
  ECGReadingsSettingCardProps
>(({ initValue }, ref) => {
  const [ecgReadingsPerDay, setECGReadingsPerDay] = useState(0);
  const [showECGReadingsPerDayError, setShowECGReadingsPerDayError] =
    useState(false);
  const [selectedOption, setSelectedOption] =
    useState<ECGReadingsOptions>("NotApplicable");

  // Set initial state when the dialog is opened.
  useEffect(() => {
    if (initValue) {
      setECGReadingsPerDay(initValue.getEcgReadingsPerDay());
      if (initValue.getEcgReadingsPerDay() > 0) {
        setSelectedOption("ECGReadingsPerDay");
      } else {
        setSelectedOption("NotApplicable");
      }
    } else {
      setECGReadingsPerDay(0);
      setSelectedOption("NotApplicable");
    }

    setShowECGReadingsPerDayError(false);
  }, [initValue]);

  const handleECGReadingsOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selected = event.target.value as ECGReadingsOptions;
    setSelectedOption(selected);

    if (selected !== "ECGReadingsPerDay") {
      setECGReadingsPerDay(0);
      setShowECGReadingsPerDayError(false);
    }
  };

  const validateECGReadings = useCallback(() => {
    if (selectedOption === "ECGReadingsPerDay") {
      const isValid = ecgReadingsPerDay > 0 && ecgReadingsPerDay <= 50;
      setShowECGReadingsPerDayError(!isValid);
      return isValid;
    }

    return true;
  }, [ecgReadingsPerDay, selectedOption]);

  const validateInput = useCallback(() => {
    return validateECGReadings();
  }, [validateECGReadings]);

  const getECGReadingsPerDay = useCallback(() => {
    return selectedOption === "ECGReadingsPerDay" ? ecgReadingsPerDay : 0;
  }, [ecgReadingsPerDay, selectedOption]);

  // Expose functions to parent components.
  useImperativeHandle(
    ref,
    () => ({
      validateInput,
      getECGReadingsPerDay,
    }),
    [getECGReadingsPerDay, validateInput]
  );

  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
        alignItems: "start",
        minWidth: compliance_setting_card_min_width,
        marginTop: "24px",
        padding: card_padding,
        borderRadius: card_border_radius,
        background: theme.palette.background.canvas,
      }}
    >
      <Typography component="h2" variant="display6" fontWeight="600">
        ECG readings (optional)
      </Typography>
      <Typography variant="caption" sx={{ margin: "8px 0px" }}>
        Select one of the two options below.
      </Typography>
      <RadioGroup
        value={selectedOption}
        onChange={handleECGReadingsOptionChange}
      >
        <FormControlLabel
          value="NotApplicable"
          control={<Radio />}
          label="Not applicable"
          aria-selected={selectedOption === "NotApplicable"}
          aria-label="ECG reading requirement not applicable"
        />

        <Stack direction="row" spacing={0}>
          <FormControlLabel
            value="ECGReadingsPerDay"
            control={<Radio />}
            label="At least"
            aria-label={` At least ${ecgReadingsPerDay} ECG readings per day`}
            aria-selected={selectedOption === "ECGReadingsPerDay"}
          />

          <NumberInput
            id="ecgReadingsPerDay"
            value={ecgReadingsPerDay}
            label="Number of"
            placeholderText="##"
            helperText={
              showECGReadingsPerDayError
                ? `The number of required ECG readings must be between 1 and 50.`
                : `Between 1 and 50`
            }
            showError={showECGReadingsPerDayError}
            onChange={(newValue) => {
              setShowECGReadingsPerDayError(false);
              setECGReadingsPerDay(newValue);
            }}
            onMouseLeave={() => {
              validateECGReadings();
            }}
          />
          <Typography variant="body1" sx={{ marginTop: "16px" }}>
            readings per day
          </Typography>
        </Stack>
      </RadioGroup>
    </Box>
  );
});
